import {
    type GenImgAttrsResult,
    type LayoutValue,
    getIntForImg,
    isBrowser,
    useGenerateImgAttrs,
} from '@app/utils/utils';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useInView } from 'react-intersection-observer';

//#region : Main component

/**
 * Custom.
 *
 * Main component parameters type.
 * Extend ImgHTMLAttributes<HTMLImageElement> with custom properties.
 */
export type ImageProps = JSX.IntrinsicElements['img'] & {
    fallbackImage?: string;
    lazy?: boolean;
    priority?: boolean;
    layout?: LayoutValue;
    quality?: number;
    unoptimized?: boolean;
};

const LazySupported = isBrowser() && 'loading' in document.createElement('img');

const defaultImgAttributes: GenImgAttrsResult = {
    src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    srcSet: undefined,
    sizes: undefined,
};

/**
 * Custom helper component.
 *
 * Use it to load image with fallback image on - error or while lazy loading.
 * If lazy loading is set, real image src is only set when the image comes in view.
 */
const Image: React.FC<ImageProps> = ({
    src,
    height,
    width,
    layout,
    fallbackImage,
    lazy,
    priority,
    quality,
    sizes,
    unoptimized,
    ...rest
}) => {
    //#region : Variables, functions and api calls

    layout = layout || (sizes ? 'responsive' : undefined);

    //InView should be triggered only once
    //as we do not want to load the image multiple times
    const [ref, inView] = useInView({
        triggerOnce: true,
    });

    const [imgLoaded, setImgLoaded] = useState(LazySupported);

    const handleImageLoad = useCallback(() => {
        setImgLoaded(true);
    }, []);

    //If there was an error loading the image, use the fallback image
    const handleImageError = useCallback(() => {
        if (fallbackImage) {
            setImgSrc(fallbackImage);
        }
    }, [fallbackImage]);

    //State to keep track of image src
    const [imgSrc, setImgSrc] = useState(() => {
        if (lazy) {
            return fallbackImage;
        }

        return src || fallbackImage;
    });

    //Before rendering, if the image is in view, set the src
    useEffect(() => {
        if (inView && src) {
            setImgSrc(src);
        }
    }, [inView, src]);

    //#endregion : Variables, functions and api calls

    const isVisible = inView || imgLoaded || LazySupported || !isBrowser();

    let imgAttributes = useGenerateImgAttrs({
        src: imgSrc ?? fallbackImage!,
        layout,
        quality: quality ?? 75,
        width: getIntForImg(width),
        unoptimized: unoptimized ?? false,
    });

    if (!isVisible) {
        imgAttributes = defaultImgAttributes;
    }

    return (
        <>
            {priority ? (
                // Note how we omit the `href` attribute, as it would only be relevant
                // for browsers that do not support `imagesrcset`, and in those cases
                // it would likely cause the incorrect image to be preloaded.
                //
                // https://html.spec.whatwg.org/multipage/semantics.html#attr-link-imagesrcset
                <Helmet defer={false}>
                    <link
                        key={'__nimg-' + imgAttributes.src + imgAttributes.srcSet + imgAttributes.sizes}
                        rel="preload"
                        as="image"
                        href={imgAttributes.src}
                        imageSrcSet={imgAttributes.srcSet}
                        imageSizes={imgAttributes.sizes}
                    ></link>
                </Helmet>
            ) : null}
            <img
                ref={ref}
                loading={lazy ? 'lazy' : undefined}
                style={{
                    width,
                    height,
                }}
                {...rest}
                width={width}
                height={height}
                onLoad={handleImageLoad}
                onError={handleImageError}
                {...imgAttributes}
            />
        </>
    );
};

export default Image;
//#endregion : Main component
